import React, { useState } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import Grid from "@mui/material/Grid";
import { useMediaQuery } from "@mui/material";
import { useField } from "formik";

import RRDDirectMailAssets from "./RRDDirectMailAssets";
import RRDDirectMailTemplate from "./RRDDirectMailTemplate";
import RRDDirectMobileTemplate from "./RRDDirectMobileTemplate";
import {
  assignAssetToRRDSlot,
  isRRDHeroLikeAssetType,
  removeAssetFromRRDSlot,
} from "@/features/campaigns/direct-mail/rrd/utils/rrdSelectionHelper";
import { normalizeRRDAssetType } from "@/features/campaigns/direct-mail/rrd/utils/rrdLayoutHelper";

type RRDTemplateProps = {
  heroImageFieldName: string;
  couponFieldName: string;
  frontCouponExpirationDateFieldName: string;
  backCouponExpirationDateFieldName: string;
  assets?: Record<string, any>;
  printType?: string | null;
  layoutKey?: string | null;
  children?: React.ReactNode;
  isBulk?: boolean;
};

type RRDDragEvent = {
  active?: { data?: { current?: Record<string, any> } };
  over?: { data?: { current?: Record<string, any> } } | null;
};

type RRDMobileSelectData = {
  sourceData: {
    type?: string;
    asset?: Record<string, any>;
  };
  targetData?: Record<string, any>;
};

function RRDTemplate({
  heroImageFieldName,
  couponFieldName,
  frontCouponExpirationDateFieldName,
  backCouponExpirationDateFieldName,
  assets,
  printType,
  layoutKey,
  children,
  isBulk = false,
}: RRDTemplateProps) {
  const [activeAsset, setActiveAsset] = useState<Record<string, any> | null>(
    null
  );
  const showDragAndDrop = useMediaQuery((theme) => theme.breakpoints.up("lg"));

  const [heroImages, , heroImageHelper] = useField(heroImageFieldName);
  const [coupons, , couponHelper] = useField(couponFieldName);
  const [frontCouponExpirationDate] = useField(
    frontCouponExpirationDateFieldName
  );
  const [backCouponExpirationDate] = useField(
    backCouponExpirationDateFieldName
  );

  const handleDragStart = (event: RRDDragEvent) => {
    setActiveAsset(event.active.data.current);
  };

  const sensors = useSensors(useSensor(PointerSensor));

  /**
   * @method handleOnDropEnd
   * @description This method is used to handle the drop event of the drag and drop component
   * @param {*} e
   * @returns
   */
  const handleOnDropEnd = (e: RRDDragEvent) => {
    const { active, over } = e;

    const sourceData = active?.data?.current;
    const targetData = over?.data?.current;

    // Early return if sourceData or targetData is missing
    if (!sourceData || !targetData) return;

    const sourceType = normalizeRRDAssetType(
      sourceData.asset_type || sourceData.assetType
    );

    if (isRRDHeroLikeAssetType(sourceType)) {
      heroImageHelper.setValue(
        assignAssetToRRDSlot(heroImages.value || [], sourceData, targetData)
      );
    } else {
      couponHelper.setValue(
        assignAssetToRRDSlot(coupons.value || [], sourceData, targetData)
      );
    }
    setActiveAsset(null);
  };

  const handleDragCancel = () => {
    setActiveAsset(null);
  };

  const handleOnSelectAsset = (data: RRDMobileSelectData) => {
    const { sourceData, targetData } = data;

    const { type: sourceType, asset } = sourceData;

    if (isRRDHeroLikeAssetType(sourceType)) {
      heroImageHelper.setValue(
        assignAssetToRRDSlot(heroImages.value || [], asset, targetData)
      );
    } else {
      couponHelper.setValue(
        assignAssetToRRDSlot(coupons.value || [], asset, targetData)
      );
    }
  };

  const handleRemoveAsset = (slot: Record<string, any>) => {
    if (isRRDHeroLikeAssetType(slot?.assetType)) {
      heroImageHelper.setValue(removeAssetFromRRDSlot(heroImages.value, slot));
    } else {
      couponHelper.setValue(removeAssetFromRRDSlot(coupons.value, slot));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleOnDropEnd}
      onDragCancel={handleDragCancel}
      autoScroll={false}
    >
      <Grid container spacing={2}>
        {showDragAndDrop ? (
          <>
            <Grid
              size={{ xs: 12, lg: 4 }}
              sx={{ zIndex: activeAsset ? 1000 : 1, position: "relative" }}
            >
              <RRDDirectMailAssets
                heroImages={heroImages.value}
                assets={assets}
                printType={printType}
                layoutKey={layoutKey}
                isBulk={isBulk}
              />
            </Grid>
          </>
        ) : null}
        <Grid size={{ xs: 12, lg: 8 }}>
          {showDragAndDrop ? (
            <RRDDirectMailTemplate
              heroImages={heroImages.value}
              coupons={coupons.value}
              fixedImages={assets?.fixedImages}
              frontCouponExpirationDate={frontCouponExpirationDate.value}
              backCouponExpirationDate={backCouponExpirationDate.value}
              printType={printType}
              layoutKey={layoutKey}
              onRemoveAsset={handleRemoveAsset}
            />
          ) : (
            <RRDDirectMobileTemplate
              heroImages={heroImages.value}
              coupons={coupons.value}
              fixedImages={assets?.fixedImages}
              onSelectImage={handleOnSelectAsset}
              frontCouponExpirationDate={frontCouponExpirationDate.value}
              backCouponExpirationDate={backCouponExpirationDate.value}
              assets={assets}
              printType={printType}
              layoutKey={layoutKey}
              onRemoveAsset={handleRemoveAsset}
              isBulk={isBulk}
            />
          )}
          {children}
        </Grid>
      </Grid>
    </DndContext>
  );
}

export default RRDTemplate;
